import { useEffect, useRef, useState } from "react";
import QRCode from "qrcode";
import { api, ApiError } from "../../../lib/api";
import { Button } from "../../ui";
import { toast } from "../../../stores/toast";

export type FlexxiCaptureField = "idFront" | "idBack" | "selfie";

export function FlexxiMobileCaptureQrPanel({
  field,
  onFileKey,
}: {
  field: FlexxiCaptureField;
  onFileKey: (key: string) => void;
}) {
  const [session, setSession] = useState<{
    token: string;
    url: string;
    expiresAt: string;
    fieldLabel: string;
  } | null>(null);
  const [qrDataUrl, setQrDataUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [waitingPhone, setWaitingPhone] = useState(false);
  const deliveredRef = useRef(false);
  const onFileKeyRef = useRef(onFileKey);
  onFileKeyRef.current = onFileKey;

  async function createSession() {
    setLoading(true);
    deliveredRef.current = false;
    setWaitingPhone(true);
    try {
      const res = await api<{
        token: string;
        url: string;
        expiresAt: string;
        fieldLabel: string;
      }>("/flexxi/kyc/capture-session", { method: "POST", body: { field } });
      setSession(res);
      const dataUrl = await QRCode.toDataURL(res.url, { width: 200, margin: 1 });
      setQrDataUrl(dataUrl);
    } catch (e) {
      toast("error", e instanceof ApiError ? e.message : "Could not create QR session");
      setWaitingPhone(false);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void createSession();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- new QR when field changes
  }, [field]);

  useEffect(() => {
    const token = session?.token;
    if (!token || deliveredRef.current) return;

    let cancelled = false;

    async function poll() {
      if (cancelled || deliveredRef.current) return;
      try {
        const res = await api<{
          status: string;
          fileKey: string | null;
        }>(`/flexxi/kyc/capture-session/${token}`);
        if (cancelled || deliveredRef.current) return;
        if (res.status === "COMPLETED" && res.fileKey) {
          deliveredRef.current = true;
          setWaitingPhone(false);
          onFileKeyRef.current(res.fileKey);
          toast("success", "Photo received from your phone");
          return;
        }
        if (res.status === "EXPIRED") {
          setWaitingPhone(false);
          toast("info", "QR expired — generating a new code");
          await createSession();
        }
      } catch {
        /* ignore transient poll errors */
      }
    }

    void poll();
    const id = setInterval(() => void poll(), 2500);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [session?.token]);

  if (!session) {
    return (
      <div className="rounded-xl border border-dashed border-white/15 bg-white/5 p-4 text-center text-xs text-white/45">
        {loading ? "Preparing QR code…" : "Could not load QR — try again"}
        {!loading && (
          <Button variant="outline" className="mt-2 text-xs" onClick={() => void createSession()}>
            Retry
          </Button>
        )}
      </div>
    );
  }

  const expiresMs = new Date(session.expiresAt).getTime() - Date.now();

  return (
    <div className="overflow-hidden rounded-xl border border-white/10 bg-white/5">
      <div className="grid gap-0 sm:grid-cols-[auto_1fr]">
        <div className="flex flex-col items-center justify-center bg-gradient-to-br from-brand-pink/90 to-brand-blue/80 p-4">
          {qrDataUrl ? (
            <img src={qrDataUrl} alt="QR code for mobile capture" className="rounded-lg bg-white p-2" width={180} height={180} />
          ) : (
            <div className="h-[180px] w-[180px] animate-pulse rounded-lg bg-white/20" />
          )}
          <p className="mt-2 max-w-[200px] text-center text-[10px] font-medium text-white/90">
            QR code expires in {Math.max(0, Math.ceil(expiresMs / 60000))} min
          </p>
        </div>
        <div className="space-y-3 p-4">
          <p className="text-xs font-semibold text-white/90">Capture on your phone</p>
          <ol className="list-inside list-decimal space-y-1.5 text-[11px] leading-relaxed text-white/45">
            <li>Scan the QR code to open the capture page for {session.fieldLabel.toLowerCase()}.</li>
            <li>Use your phone camera — ensure the document is clear and fully in frame.</li>
            <li>When you finish on your phone, the photo appears here automatically.</li>
          </ol>
          {waitingPhone ? <p className="text-[11px] text-brand-pink">Waiting for upload from your phone…</p> : null}
          <Button variant="ghost" className="text-xs" disabled={loading} onClick={() => void createSession()}>
            New QR
          </Button>
        </div>
      </div>
    </div>
  );
}
